<template>
  <div>
    <h1>Transferências Discricionárias e Legais</h1>
    <b-card>
      <div class="d-flex mb-2 w-100">
        <chips-status v-model="params" @change="handleFiltersChange" />
      </div>
      <b-table
        hover
        :items="items"
        :fields="fields"
        :busy="isBusy"
        @sort-changed="handleSortChanged"
        show-empty
        sort-icon-left
        class="list-oportunidades"
      >
        <template #head(id)="{ label }">
          <b-checkbox
            v-if="selectedItems.length"
            :checked="allPageSelected"
            :indeterminate="somePageSelected"
            :disabled="isUpdating"
            inline
            class="mx-0 pl-0"
            aria-label="Selecionar itens da página"
            @change="togglePage"
          />
          <span v-else v-text="label" />
        </template>
        <template #cell(id)="{ item }">
          <b-checkbox
            :checked="selectedItems.includes(item.id)"
            :disabled="isUpdating"
            inline
            class="mx-0 pl-0"
            :aria-label="`Selecionar oportunidade ${item.id}`"
            @change="toggleItem(item.id, $event)"
          />
        </template>
        <template #cell(nome)="{ item }">
          <router-link
            :to="{ name: 'oportunidades_edit', params: { id: item.id } }"
          >
            <h5 class="mb-1" v-text="item.nome" />
            <span class="text-muted">#{{ item.codigo }}</span>
          </router-link>
        </template>

        <template #cell(status_exibicao)="{ value }">
          <div class="d-inline-flex align-content-center">
            <span class="mr-1 text-exitos">
              <font-awesome-icon :icon="value.icon" />
            </span>
            <span v-text="value.label" />
          </div>
        </template>

        <template #cell(created_at)="{ item }">
          {{ item.created_at | format_datetime }}
        </template>

        <template #cell(actions)="row">
          <Actions
            :item="row.item"
            :view_show="false"
            :view_edit="$can('oportunidade:edit')"
            edit_to="oportunidades_edit"
          />
        </template>

        <template #empty="{}">
          <h5 class="text-center">Nenhum item encontado</h5>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
    </b-card>
    <batch-selector
      v-model="selectedItems"
      :visible.sync="batchVisible"
      :confirm-disabled="!batchStatus"
      :busy="isUpdating"
      :hidden-confirm="!isSingleSelection && !showStatusPanel"
      @get-pannel-ref="($event) => (statusRef = $event)"
      @cancel="clearSelection"
      @confirm="confirmBatch"
    >
      <div
        v-if="!isSingleSelection && showStatusPanel"
        class="overlay-floating"
        @click="toggleStatusPanel(false)"
      />
      <div
        class="d-inline-flex align-items-center flex-grow-1 justify-content-center mx-4"
        :aria-expanded="!isSingleSelection && showStatusPanel"
      >
        <template v-if="isSingleSelection">
          <label for="single-extraction-status" class="mr-2 mb-0">
            Status
          </label>
          <b-form-select
            id="single-extraction-status"
            v-model="batchStatus"
            :options="statusOptions"
            :disabled="isUpdating"
            class="single-status-select"
          />
        </template>
        <b-button
          v-else
          variant="primary"
          @click="toggleStatusPanel"
          class="mr-0"
          :disabled="showStatusPanel"
        >
          <font-awesome-icon icon="sliders" />
          Alterar status
        </b-button>
        <div
          ref="floatingRef"
          v-if="!isSingleSelection && showStatusPanel"
          :style="floatingStyles"
          class="custom-panel-floating bg-white rounded-lg"
        >
          <div class="px-3 py-4">
            <b-form-group
              label="Status"
              description="O status selecionado será aplicado a todas as oportunidades selecionadas. Confirme se a seleção está correta antes de continuar."
            >
              <b-form-select
                v-model="batchStatus"
                :options="statusOptions"
                :disabled="isUpdating"
                aria-label="Status da extração em lote"
              />
            </b-form-group>
          </div>
        </div>
      </div>
    </batch-selector>
    <div>
      <pagination-nav :pagination="pagination" @input="handlePageChange" />
    </div>
  </div>
</template>

<script>
import moment from "moment";

import ChipsStatus from "@/modules/extratores/Components/ChipsStatus.vue";
import { deepCopy } from "@/helpers/functions/ObjectHelpers";
import { capitalize } from "@/helpers/functions/StringHelpers";
import { computed, defineComponent, ref, watch } from "vue";
import { useTableList } from "@/composables/useTableList";
import { useNotify } from "@/composables/useNotify";
import { extractErrorMessage } from "@/helpers/functions/errorHelpers";
import { useActions, useNamespacedState } from "vuex-composition-helpers";
import Actions from "@/components/Table/Actions.vue";
import BatchSelector from "@/components/BatchSelector.vue";
import { useFloating } from "@floating-ui/vue";
import { useToggle } from "@vueuse/core";

export default defineComponent({
  name: "ListExtratores",
  components: {
    ChipsStatus,
    BatchSelector,
    Actions,
  },
  setup() {
    const selectedItems = ref([]);
    const batchVisible = ref(false);
    const batchStatus = ref(null);
    const isUpdating = ref(false);
    const [showStatusPanel, toggleStatusPanel] = useToggle(false);
    const notifications = useNotify();
    const statusOptions = [
      { value: null, text: "Selecione o status", disabled: true },
      { value: "encontrada", text: "Encontrada" },
      { value: "nao_encontrada", text: "Não encontrada" },
      { value: "ignorada", text: "Ignorada" },
    ];
    const params = ref({ status_extracao: null, status_exibicao: [] });

    const statusRef = ref(null);
    const floatingRef = ref(null);

    const { floatingStyles } = useFloating(statusRef, floatingRef, {
      placement: "top",
      open: showStatusPanel,
    });

    const { extratores_list: items, pagination } = useNamespacedState(
      "extratores",
      ["extratores_list", "pagination"]
    );

    const {
      ActionList,
      ActionDelete,
      ActionSetPagination,
      ActionUpdateExtractionStatus,
    } = useActions("extratores", [
      "ActionList",
      "ActionDelete",
      "ActionSetPagination",
      "ActionUpdateExtractionStatus",
    ]);

    const pageIds = computed(() =>
      Array.isArray(items.value) ? items.value.map((item) => item.id) : []
    );

    const allPageSelected = computed(
      () =>
        pageIds.value.length > 0 &&
        pageIds.value.every((id) => selectedItems.value.includes(id))
    );

    const somePageSelected = computed(
      () =>
        !allPageSelected.value &&
        pageIds.value.some((id) => selectedItems.value.includes(id))
    );

    const isSingleSelection = computed(() => selectedItems.value.length === 1);

    watch(selectedItems, (ids) => {
      batchVisible.value = ids.length > 0;
      if (ids.length <= 1) toggleStatusPanel(false);
      if (!ids.length) batchStatus.value = null;
    });

    const queryParams = computed(() => {
      const { status_extracao, status_exibicao } =
        deepCopy(params.value) || {};

      return {
        status_extracao: status_extracao || null,
        status_exibicao: status_exibicao || [],
      };
    });

    const table = useTableList({
      pagination,
      fetchItems: ActionList,
      removeItem: ActionDelete,
      setPagination: ActionSetPagination,
      params: queryParams,
    });

    const fields = computed(() => [
      {
        key: "id",
        label: "COD",
        class:
          "col-auto px-4 h5 mb-0 text-center align-middle custom-id-column",
        formatter: (value) => `#${value}`,
      },
      { key: "nome", label: "Nome da oportunidade", class: "align-middle" },
      {
        key: "qualificacao_propostas",
        label: "Qualificação da proposta",
        class: "align-middle",
        formatter: (value, head, object) => {
          const tipos = {
            "emenda-parlamentar": "Emenda Parlamentas",
            voluntaria: "Voluntária",
            "especifica-concedente": "Específica Concedente",
          };

          const key = Array.isArray(value) ? value[0] : value;
          return tipos[key] || "Indeterminada";
        },
      },
      {
        key: "data_proposta_fim",
        label: "Prazo",
        class: "col-2 align-middle",
        formatter: (value, head, object) => {
          if (!value) return "-";

          return moment(value).format("DD/MM/YYYY");
        },
      },
      {
        key: "status_exibicao",
        label: "Status da oportunidade",
        class: "col-1 align-middle",
        formatter: (value) => {
          switch (value) {
            case "visivel":
              return {
                icon: "eye",
                label: capitalize(value),
              };

            case "arquivado":
              return {
                icon: "box-archive",
                label: capitalize(value),
              };

            case "edicao":
              return {
                icon: "pen-to-square",
                label: "Edição",
              };

            default:
              return {
                icon: "file-circle-question",
                label: "Não Visível",
              };
          }
        },
      },
      {
        key: "created_at",
        label: "Data da extração",
        class: "col-1 align-middle",
        sortable: true,
      },
      { key: "actions", label: "", class: "cell-actions" },
    ]);

    function handleFiltersChange(props) {
      clearSelection();
      params.value = props;
      pagination.value.current_page = 1;

      table.loadData();
    }

    function toggleItem(id, checked) {
      const items = deepCopy(selectedItems.value);

      selectedItems.value = checked
        ? [...new Set([...items, id])]
        : items.filter((selectedId) => selectedId !== id);
    }

    function togglePage(checked) {
      const items = deepCopy(selectedItems.value);

      selectedItems.value = checked
        ? [...new Set([...items, ...pageIds.value])]
        : items.filter((id) => !pageIds.value.includes(id));
    }

    function clearSelection() {
      toggleStatusPanel(false);
      selectedItems.value = [];
      batchStatus.value = null;
      batchVisible.value = false;
    }

    function handlePageChange() {
      clearSelection();
      table.loadData();
    }

    function handleSortChanged(sort) {
      clearSelection();
      table.sortingChanged(sort);
    }

    async function confirmBatch(ids) {
      if (!ids.length || !batchStatus.value || isUpdating.value) return;

      isUpdating.value = true;

      try {
        const response = await ActionUpdateExtractionStatus({
          status_extracao: batchStatus.value,
          oportunidades: ids,
        });
        const result = response.data || {};
        const updated = result.ids_atualizados || [];
        const ignored = result.ids_ignorados || [];
        const updatedLabel =
          updated.length === 1 ? "item atualizado" : "itens atualizados";
        const ignoredLabel =
          ignored.length === 1 ? "ID ignorado" : "IDs ignorados";

        clearSelection();
        await table.loadData(true);

        notifications?.addNotification({
          message: `${updated.length} ${updatedLabel}; ${ignored.length} ${ignoredLabel}.`,
          type: "success",
        });
      } catch (error) {
        notifications?.addNotification({
          message: extractErrorMessage(
            error,
            "Erro ao atualizar o status da extração"
          ),
          type: "danger",
        });
      } finally {
        isUpdating.value = false;
      }
    }

    return {
      fields,
      items,
      pagination,
      params,
      selectedItems,
      isSingleSelection,
      batchVisible,
      batchStatus,
      statusOptions,
      isUpdating,
      allPageSelected,
      somePageSelected,
      floatingStyles,
      statusRef,
      floatingRef,
      showStatusPanel,
      toggleStatusPanel,
      ...table,
      handleFiltersChange,
      toggleItem,
      togglePage,
      clearSelection,
      handlePageChange,
      handleSortChanged,
      confirmBatch,
    };
  },
});
</script>

<style lang="scss">
.list-oportunidades {
  tr {
    th {
      white-space: normal !important;
    }
  }

  .custom-control-label {
    min-width: 21px;
  }

  .custom-control-label::before,
  .custom-control-label::after {
    left: 0;
  }

  .cell-rec_propostas {
    font-weight: bold;
    border: 1px solid #ddd;
    padding: 0.3rem;
    text-align: center;
    color: #f5365c;
    text-transform: uppercase;

    &.cell-rec_propostas-sim {
      color: #2dce89;
    }
  }
}

.custom-id-column {
  min-width: 0;
  width: 85px;
}

.single-status-select {
  min-width: 180px;
  max-width: 260px;
}

.custom-border {
  position: relative;
  height: 2rem;
  min-height: 1px;
  width: 1px;
}

.overlay-floating {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(var(--dark-rgb), 0.1);
  z-index: -2;
}

.batch-custom {
  &:has([aria-expanded="true"]) {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;

    .custom-panel-floating {
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }
  }
}

.custom-panel-floating {
  z-index: -1;
  width: 100%;
  box-shadow: 1rem 0 2rem rgba(var(--dark-rgb), 0.1);
}
</style>
